import * as React from "react"
import { useIntersectionObserver } from "../hooks/useIntersectionObserver.ts"

const Provider = ({ children }: FeedMediaController.Provider.Props) => {
  const [state, setState] = React.useState<FeedMediaControllerState>(FeedMediaControllerState.init())

  const addMedia = (id: string, controls: FeedMediaController.Controls) => {
    setState((prev) => prev.addMedia(id, controls))
  }

  const removeMedia = (id: string) => {
    setState((prev) => prev.removeMedia(id))
  }

  const addInViewMedia = (id: string, el: HTMLDivElement) => {
    setState((prev) => prev.addInViewMedia(id, el))
  }

  const removeInViewMedia = (id: string) => {
    setState((prev) => prev.removeInViewMedia(id))
  }

  const playMedia = (id: string) => {
    setState((prev) => prev.playMedia(id))
  }

  const pauseMedia = (id: string) => {
    setState((prev) => prev.pauseMedia(id))
  }

  const setMuted = (muted: boolean) => {
    setState((prev) => prev.setMuted(muted))
  }

  React.useEffect(() => {
    const checkClosestMedia = () => {
      const media = [...state.inViewMediaIds.entries()]

      const viewportYCenter = window.innerHeight / 2

      const getElementYCenter = (element: HTMLDivElement) => {
        const boundingRect = element.getBoundingClientRect()

        return boundingRect.top + boundingRect.height / 2
      }

      const closestMedia = media
        .map(([id, el]) => {
          const elementYCenter = getElementYCenter(el)
          const distance = Math.abs(elementYCenter - viewportYCenter)

          return {
            id,
            distance
          }
        })
        .sort((a, b) => a.distance - b.distance)[0]

      if (closestMedia && closestMedia.id !== state.activeId) {
        playMedia(closestMedia.id)
      }
    }

    // Check closest media whenever `inViewMediaIds` changes.
    // This is needed for whenever a media comes in view without the scroll position changing.
    checkClosestMedia()

    window.addEventListener("scroll", checkClosestMedia)
    window.addEventListener("resize", checkClosestMedia)

    return () => {
      window.removeEventListener("scroll", checkClosestMedia)
      window.removeEventListener("resize", checkClosestMedia)
    }
  }, [state.inViewMediaIds])

  return (
    <Context.Provider
      value={{
        state,
        addMedia,
        removeMedia,
        addInViewMedia,
        removeInViewMedia,
        playMedia,
        pauseMedia,
        setMuted
      }}
    >
      {children}
    </Context.Provider>
  )
}

const Observer = ({
  id,
  children,
  paused,
  className
}: {
  id: string
  children: React.ReactNode
  paused?: boolean
  className?: string
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const context = useInternalContext()
  const { isInView, ref: intersectionRef } = useIntersectionObserver({ threshold: 0.6 })

  const callbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      intersectionRef(node)
      containerRef.current = node
    },
    [isInView]
  )

  React.useEffect(() => {
    if (isInView && !paused && containerRef.current) {
      context.addInViewMedia(id, containerRef.current)
    } else {
      context.pauseMedia(id)
      context.removeInViewMedia(id)
    }
  }, [isInView, id, paused])

  return (
    <div ref={callbackRef} className={className}>
      {children}
    </div>
  )
}

export function useFeedMediaController(
  mediaId: string,
  controls: FeedMediaController.Controls
): FeedMediaController.UseFeedMediaController.ReturnType {
  const context = useInternalContext()

  React.useEffect(() => {
    context.addMedia(mediaId, controls)

    return () => {
      context.removeMedia(mediaId)
    }
  }, [mediaId])

  return {
    isActive: context.state.activeId === mediaId,
    globalMuteState: context.state.isMuted,
    setMuted: context.setMuted,
    playMedia: context.playMedia
  }
}

export class FeedMediaControllerState {
  static init(): FeedMediaControllerState {
    return new FeedMediaControllerState(null, true, new Map(), new Map())
  }

  private constructor(
    private readonly currentActiveId: string | null,
    private readonly muted: boolean,
    private readonly allMedia: Map<string, FeedMediaController.Controls>,

    public readonly inViewMediaIds: Map<string, HTMLDivElement>
  ) {}

  private setActiveMedia(id: string): FeedMediaControllerState {
    if (this.currentActiveId === id) return this

    return new FeedMediaControllerState(id, this.muted, this.allMedia, this.inViewMediaIds)
  }

  addMedia(id: string, controls: FeedMediaController.Controls): FeedMediaControllerState {
    const newMedia = new Map(this.allMedia)
    newMedia.set(id, controls)

    return new FeedMediaControllerState(this.currentActiveId, this.muted, newMedia, this.inViewMediaIds)
  }

  removeMedia(id: string): FeedMediaControllerState {
    const newMedia = new Map(this.allMedia)
    newMedia.delete(id)

    const newActiveId = this.currentActiveId === id ? null : this.currentActiveId

    return new FeedMediaControllerState(newActiveId, this.muted, newMedia, this.inViewMediaIds)
  }

  addInViewMedia(id: string, ref: HTMLDivElement): FeedMediaControllerState {
    const newInViewMediaIds = new Map(this.inViewMediaIds)
    newInViewMediaIds.set(id, ref)

    return new FeedMediaControllerState(this.currentActiveId, this.muted, this.allMedia, newInViewMediaIds)
  }

  removeInViewMedia(id: string): FeedMediaControllerState {
    const newInViewMediaIds = new Map(this.inViewMediaIds)
    newInViewMediaIds.delete(id)

    return new FeedMediaControllerState(this.currentActiveId, this.muted, this.allMedia, newInViewMediaIds)
  }

  playMedia(id: string): FeedMediaControllerState {
    const nextControls = this.allMedia.get(id)
    if (!nextControls) return this

    if (!this.inViewMediaIds.has(id)) {
      nextControls.pause()
      return this
    }

    if (this.currentActiveId === id) {
      return this
    }

    if (this.currentActiveId) {
      const prevControls = this.allMedia.get(this.currentActiveId)
      prevControls?.pause()
    }

    nextControls.play()

    if (this.muted) {
      nextControls.mute()
    } else {
      nextControls.unmute()
    }

    return this.setActiveMedia(id)
  }

  pauseMedia(id: string): FeedMediaControllerState {
    if (this.currentActiveId === id) {
      const mediaControls = this.allMedia.get(id)
      mediaControls?.pause()
      return new FeedMediaControllerState(null, this.muted, this.allMedia, this.inViewMediaIds)
    }
    return this
  }

  setMuted(muted: boolean): FeedMediaControllerState {
    return new FeedMediaControllerState(this.currentActiveId, muted, this.allMedia, this.inViewMediaIds)
  }

  get activeId() {
    return this.currentActiveId
  }

  get isMuted() {
    return this.muted
  }
}

const Context = React.createContext<FeedMediaController.Context>({
  state: FeedMediaControllerState.init(),
  addMedia: () => {},
  removeMedia: () => {},
  addInViewMedia: () => {},
  removeInViewMedia: () => {},
  playMedia: () => {},
  pauseMedia: () => {},
  setMuted: () => {}
})
const useInternalContext = () => React.use(Context)

export const FeedMediaController = {
  Provider,
  Observer,

  useFeedMediaController
}

export namespace FeedMediaController {
  export namespace Provider {
    export type Props = {
      children: React.ReactNode
    }
  }

  export type Context = {
    state: FeedMediaControllerState
    addMedia: (id: string, controls: Controls) => void
    removeMedia: (id: string) => void
    addInViewMedia: (id: string, el: HTMLDivElement) => void
    removeInViewMedia: (id: string) => void
    playMedia: (id: string) => void
    pauseMedia: (id: string) => void
    setMuted: (muted: boolean) => void
  }

  export type Controls = {
    play: () => void
    pause: () => void
    mute: () => void
    unmute: () => void
  }

  export namespace UseFeedMediaController {
    export type ReturnType = {
      isActive: boolean
      globalMuteState: boolean
      setMuted: (muted: boolean) => void
      playMedia: (id: string) => void
    }
  }

  export namespace UseMediaIntersection {
    export type ReturnType = {
      isInView: boolean
      ref: (node: Element | null) => void
    }
  }
}
